/**
 * Utility functions for mapping Firebase Auth error codes to user-friendly messages
 */
/**
 * Map Firebase error codes to user-friendly messages for email/password login.
 */
function getLoginErrorMessage(errorCode) {
    const errorMessages = {
        'auth/user-not-found': 'Invalid email or password. Please try again.',
        'auth/wrong-password': 'Invalid email or password. Please try again.',
        'auth/invalid-credential': 'Invalid email or password. Please try again.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled. Please contact support for assistance.',
        'auth/too-many-requests': 'Too many failed sign-in attempts. Please wait a few minutes and try again.',
        'auth/network-request-failed': 'Unable to connect. Please check your internet connection and try again.',
    };
    return errorMessages[errorCode] || 'Sign-in failed. Please try again.';
}
/**
 * Map Firebase invitation error codes to user-friendly messages
 * Used by InviteAuthGuard to display invite-specific error messages
 */
function getInvitationErrorMessage(errorCode) {
    const errorMessages = {
        'auth/user-not-found': 'Invalid invitation. Please check your invitation link.',
        'auth/wrong-password': 'Invalid or expired invitation token.',
        'auth/invalid-email': 'Please check your invitation link for a valid email address.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
        'auth/invalid-credential': 'Invalid invitation. Please check your invitation link and try again.',
    };
    return errorMessages[errorCode] || 'Authentication failed. Please check your invitation link.';
}
/**
 * Map Firebase custom token error codes to user-friendly messages
 * Used by AuthGuard to display custom-token-specific error messages
 */
function getCustomTokenErrorMessage(errorCode) {
    const errorMessages = {
        'auth/custom-token-mismatch': 'Invalid access token. The token was not issued for this project.',
        'auth/invalid-custom-token': 'Invalid access token format. Please check the token and try again.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/custom-token-expired': 'Access token has expired. Please request a new token.',
    };
    return (errorMessages[errorCode] || 'Token authentication failed. Please check your access token.');
}
/**
 * Map Firebase password reset error codes to user-friendly messages
 */
function getPasswordResetErrorMessage(errorCode) {
    const errorMessages = {
        'auth/invalid-email': 'The email address is not valid.',
        'auth/expired-action-code': 'This reset link has expired. Please request a new one.',
        'auth/invalid-action-code': 'It may have already been used or is no longer active. Request a new link to continue.',
        'auth/weak-password': 'Password is too weak. Please choose a stronger password.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
    };
    return errorMessages[errorCode] || 'Failed to reset password. Please try again.';
}
/**
 * Map Firebase error codes to user-friendly messages for password update
 */
function getPasswordUpdateErrorMessage(errorCode) {
    const errorMessages = {
        'auth/weak-password': 'Password is too weak. Please choose a stronger password.',
        'auth/requires-recent-login': 'For security, please sign out and sign back in before changing your password.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
    };
    return errorMessages[errorCode] || 'Failed to update password. Please try again.';
}
/**
 * Map Firebase email verification error codes to user-friendly messages
 */
function getEmailVerificationErrorMessage(errorCode) {
    const errorMessages = {
        'auth/expired-action-code': 'This verification link has expired. Please request a new one.',
        'auth/invalid-action-code': 'This verification link is invalid or has already been used. Please request a new one.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
    };
    return errorMessages[errorCode] || 'Failed to verify email. Please try again.';
}
/**
 * Map Firebase MFA error codes to user-friendly messages
 */
function getMfaErrorMessage(errorCode) {
    const errorMessages = {
        'auth/invalid-verification-code': 'The code you entered is incorrect. Please try again.',
        'auth/code-expired': 'The verification code has expired. Please request a new code.',
        'auth/missing-verification-code': 'Please enter the verification code.',
        'auth/invalid-verification-id': 'Verification session expired. Please start over.',
        'auth/quota-exceeded': 'Too many SMS requests. Please wait before trying again.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
        'auth/captcha-check-failed': 'Security verification failed. Please refresh and try again.',
        'auth/invalid-phone-number': 'Invalid phone number on file.',
    };
    return errorMessages[errorCode] || 'Verification failed. Please try again.';
}
/**
 * Gets user-friendly error messages for common Firebase MFA SMS enrollment errors
 */
function getMfaSmsErrorMessage(code) {
    switch (code) {
        case 'auth/invalid-phone-number':
            return 'Please enter a valid phone number';
        case 'auth/too-many-requests':
            return 'Too many requests. Please wait a minute before trying again.';
        case 'auth/invalid-verification-code':
            return 'Invalid verification code. Please check and try again.';
        case 'auth/code-expired':
            return 'Verification code has expired. Please request a new code.';
        case 'auth/session-expired':
            return 'Session expired. Please start over.';
        case 'auth/quota-exceeded':
            return 'SMS quota exceeded. Please try again later.';
        case 'auth/captcha-check-failed':
            return 'reCAPTCHA verification failed. Please try again.';
        default:
            return null;
    }
}
/**
 * Check if an error requires re-authentication before proceeding.
 * Matches both:
 *   - auth/requires-recent-login — session is too old for sensitive operations
 *   - auth/unsupported-first-factor — session was created via custom token,
 *     which Firebase does not accept as a first factor for MFA enrollment.
 *     Re-authenticating with email/password switches the first factor.
 */
function isRequiresRecentLoginError(error) {
    const code = error?.code;
    return code === 'auth/requires-recent-login' || code === 'auth/unsupported-first-factor';
}

export { getCustomTokenErrorMessage, getEmailVerificationErrorMessage, getInvitationErrorMessage, getLoginErrorMessage, getMfaErrorMessage, getMfaSmsErrorMessage, getPasswordResetErrorMessage, getPasswordUpdateErrorMessage, isRequiresRecentLoginError };
//# sourceMappingURL=authErrorMessages.js.map
